import { For, JSX, Show, createMemo } from 'solid-js';
import { MenuDownIcon, MenuUpIcon } from '@atgfr8/icons';
import './Styles.css';

export type MenuItemType = {
  label: string | JSX.Element;
  value: string;
  disabled?: boolean;
};

export type MenuGroupType = {
  label: string;
  items: MenuItemType[];
};

export type MenuItems = MenuItemType[] | MenuGroupType[];

const isGrouped = (items: MenuItems): items is MenuGroupType[] =>
  items.length > 0 && 'items' in items[0];

type SelectInputProps = {
  label?: string;
  menuItems: MenuItems;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  error?: string | null;
  style?: JSX.CSSProperties;
  required?: boolean;
  dataTestId: string;
  size?: 'sm' | 'md' | 'lg';
  class?: string;
  searchable?: boolean;
};

type NormalizedGroup = { label?: string; items: MenuItemType[] };

export const SelectInput = (props: SelectInputProps) => {
  const hasError = () => Boolean(props.error);
  const normalizedValue = () => String(props.value ?? '');

  const heightClass = () => {
    if (props.size === 'sm') return 'h-[32px]  text-xs';
    if (props.size === 'md') return 'h-[40px]';
    if (props.size === 'lg') return 'h-[48px]';
    return 'h-[40px]';
  };

  const normalizedGroups = createMemo<NormalizedGroup[]>(() => {
    const items = props.menuItems;
    if (isGrouped(items)) {
      return items.map((g) => ({
        label: g.label,
        items: (g.items as MenuItemType[] | undefined) ?? [],
      }));
    }
    return [{ label: undefined, items: items as MenuItemType[] }];
  });

  const allItems = createMemo(() => normalizedGroups().flatMap((g) => g.items));

  const hasMatchingMenuItem = () => {
    const value = normalizedValue();
    if (!value) return false;
    return allItems().some((item) => item.value === value);
  };

  return (
    <div class="relative w-full min-w-0">
      <Show when={props.menuItems} keyed>
        <div class="relative w-full min-w-0">
          <select
            style={props.style ?? undefined}
            data-testid={props.dataTestId}
            name={props.name}
            required={props.required ?? false}
            value={normalizedValue()}
            onChange={(e) => props.onChange?.(e.currentTarget.value)}
            onBlur={() => props.onBlur?.()}
            onKeyDown={(e) => {
              try {
                if (e.currentTarget.matches(':open') || e.key !== 'Enter')
                  return;
                e.preventDefault();
                e.currentTarget.showPicker();
              } catch {}
            }}
            disabled={props.disabled}
            aria-invalid={hasError() || undefined}
            class={`select-input cursor-pointer peer w-full appearance-none pl-3 pr-10 py-2 border rounded-sm shadow-xs
           focus:outline-hidden disabled:border-gray-300
           ${heightClass()}
           ${props.disabled ? 'select-input-disabled' : ''}
           ${
             hasError() && !props.disabled
               ? 'border-error-red text-[#b00020]!'
               : 'border-input-border hover:border-armstrong-primary focus:border-armstrong-primary'
           }
          ${
            normalizedValue() !== '' && !props.disabled
              ? 'select-input-has-value'
              : ''
          }
         ${props.class}
           `}
          >
            <Show when={props.placeholder}>
              <option value="">{props.placeholder ?? '-'}</option>
            </Show>
            <Show when={!props.placeholder && !normalizedValue()}>
              <option value="" hidden />
            </Show>
            <Show when={normalizedValue() && !hasMatchingMenuItem()}>
              <option value={normalizedValue()} hidden>
                {normalizedValue()}
              </option>
            </Show>
            <For each={allItems()}>
              {(item) => (
                <option value={item.value} disabled={item.disabled}>
                  {item.label}
                </option>
              )}
            </For>
          </select>
          <MenuDownIcon
            size="18px"
            color={props.disabled ? '#9ca3af' : '#757575'}
            class="select-input-icon select-input-icon--down z-[1]"
          />
          <MenuUpIcon
            size="18px"
            color={props.disabled ? '#9ca3af' : '#757575'}
            class="select-input-icon select-input-icon--up z-[1]"
          />
          <Show when={props.label}>
            <label
              class={`select-input-label absolute -top-2 left-2 px-1 text-xs font-medium pointer-events-none
            overflow-hidden whitespace-nowrap text-ellipsis max-w-[90%] ${
              hasError() ? 'text-error-red' : 'text-armstrong-primary'
            }`}
            >
              {props.label}
              {props.required && <span> *</span>}
            </label>
          </Show>
        </div>
        {hasError() && (
          <div class={'text-error-red text-xs mt-1 px-2 '}>{props.error}</div>
        )}
      </Show>
    </div>
  );
};
